import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

import '../MultiPlayerPostRoll/MultiPlayerPostRoll.css';
import './TiebreakerPostRoll.css';
import {
  getTiebreakerWinner,
  handleHomeClick,
  playSound,
} from '../../utils/utils';
import Button from '../../components/Button/Button';
import SaveGameControl from '../../components/SaveGameControl/SaveGameControl';
import MultiplayerRollReveal from '../../components/MultiplayerRollReveal/MultiplayerRollReveal';
import MultiplayerResultModal from '../../components/MultiplayerResultModal/MultiplayerResultModal';
import WarpLink from '../../components/WarpTransition/WarpLink';

const TiebreakerPostRoll = ({
  playerNamesState,
  tiebreakerState,
  initializeTiebreakerPlayers,
}) => {
  const [revealComplete, setRevealComplete] = useState(false);
  const [resultOpen, setResultOpen] = useState(false);
  const rollKey = tiebreakerState
    .map((player) => `${player.id}:${player.rolls[player.rolls.length - 1]}`)
    .join('|');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleRevealComplete = useCallback(() => {
    setRevealComplete(true);
    setResultOpen(true);
  }, []);

  const winnerData = useMemo(
    () =>
      tiebreakerState.length &&
      tiebreakerState.every((player) => player.rolls.length)
        ? getTiebreakerWinner(tiebreakerState)
        : null,
    [tiebreakerState]
  );

  if (!winnerData) {
    return (
      <div className="TiebreakerPostRoll multiplayerResultsPage tiebreakerResultsPage">
        <div className="multiplayerResultsPage__empty">
          <h1>No active tiebreaker</h1>
          <Link to="/multi/pre-roll">
            <Button title="back to game" type="smallSquare" />
          </Link>
        </div>
      </div>
    );
  }

  const [winningPlayer, winningNumber, , tieWinners] = winnerData;
  const isTie = tieWinners.length > 1;

  const handleTiebreakerRematch = () => {
    window.scrollTo(0, 0);
    playSound('/sounds/futuristic_spinning.mp3');
    initializeTiebreakerPlayers(tieWinners);
  };

  const handleRematch = () => {
    window.scrollTo(0, 0);
    playSound('/sounds/charged-laser-sound.mp3');
  };

  return (
    <div className="TiebreakerPostRoll multiplayerResultsPage tiebreakerResultsPage">
      <Link to="/">
        <div className="header">
          <Button
            title="home"
            type="smallSquare home"
            handleClick={handleHomeClick}
          />
        </div>
      </Link>

      <main className="multiplayerResultsPage__content">
        <p className="multiplayerResultsPage__eyebrow">SUDDEN DEATH</p>
        <h1 className="multiplayerResultsPage__title">tiebreaKer</h1>
        <p className="multiplayerResultsPage__subtitle">
          One locked number decides who survives the standoff.
        </p>

        <MultiplayerRollReveal
          key={rollKey}
          players={tiebreakerState}
          onComplete={handleRevealComplete}
          variant="tiebreaker"
        />

        <MultiplayerResultModal
          isOpen={resultOpen}
          isTie={isTie}
          kicker={isTie ? 'STILL DEADLOCKED' : 'TIEBREAKER CHAMPION'}
          headline={isTie ? 'AGAIN!' : `${winningPlayer} wins!`}
          winningNumber={winningNumber}
          tieWinners={tieWinners}
          primaryLabel={isTie ? 'Reignite Tiebreaker' : 'new round'}
          primaryTo={isTie ? '/multi/tiebreaker/pre-roll' : '/multi/pre-roll'}
          primaryTransitionLabel={
            isTie ? 'SUDDEN DEATH RECHARGED' : 'ARENA RESETTING'
          }
          primaryTransitionVariant={isTie ? 'danger' : 'roll'}
          onPrimary={isTie ? handleTiebreakerRematch : handleRematch}
          onClose={() => setResultOpen(false)}
        />

        {revealComplete && !isTie && !resultOpen && (
          <motion.div
            className="multiPlayerButtons multiplayerResultsPage__actions"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <WarpLink
              to="/multi/pre-roll"
              label="ARENA RESETTING"
              variant="roll"
              onClick={handleRematch}
            >
              <Button title="new round" type="smallSquare rematch" />
            </WarpLink>
            <SaveGameControl playerNamesState={playerNamesState} />
          </motion.div>
        )}
      </main>
    </div>
  );
};

export default TiebreakerPostRoll;
